import { useCallback } from 'react';
import { Location, matchPath, useLocation } from 'react-router';

import { AppObject, TeamObject } from '@api/types';
import { getLocalTeamState } from '@routes/RedirectToFirstTeam';

function matchTeamPath(path: string, location: Location) {
  return matchPath(`/:teamUuid${path}`, location.pathname);
}

function matchAppPath(path: string, location: Location) {
  return matchPath(`/:teamUuid/:appUuid${path}`, location.pathname);
}

function getMatchingTeamAppPath(
  teamUuid: string,
  appUuid: string,
  location: Location
) {
  const settingsMatch = matchTeamPath('/settings/*', location);
  if (settingsMatch) {
    const subpath = settingsMatch.pathname.split('/settings/')[1];
    return `/${teamUuid}/settings${subpath ? `/${subpath}` : ''}`;
  }

  if (matchAppPath('/relays/*', location)) {
    return `/${teamUuid}/${appUuid}/relays`;
  } else if (matchAppPath('/functions/*', location)) {
    return `/${teamUuid}/${appUuid}/functions`;
  } else if (matchAppPath('/enclaves/*', location)) {
    return `/${teamUuid}/${appUuid}/enclaves`;
  } else if (matchAppPath('/payments/*', location)) {
    return `/${teamUuid}/${appUuid}/payments`;
  } else if (matchAppPath('/logs/*', location)) {
    return `/${teamUuid}/${appUuid}/logs`;
  } else if (matchAppPath('/policies/*', location)) {
    return `/${teamUuid}/${appUuid}/policies`;
  } else if (matchAppPath('/compliance/*', location)) {
    return `/${teamUuid}/${appUuid}/compliance`;
  } else if (matchAppPath('/asv/*', location)) {
    return `/${teamUuid}/${appUuid}/asv`;
  } else if (matchAppPath('/settings/*', location)) {
    return `/${teamUuid}/${appUuid}/settings`;
  } else {
    return `/${teamUuid}/${appUuid}`;
  }
}

export function useTeamAppSwitch(teamUuid: string) {
  const location = useLocation();

  const getAppPath = useCallback(
    (app: AppObject) => {
      return getMatchingTeamAppPath(teamUuid, app.uuid, location);
    },
    [teamUuid, location]
  );

  const getTeamPath = useCallback(
    (team: TeamObject) => {
      const firstApp = team.apps.at(0);
      const teamState = getLocalTeamState(team.uuid);

      let app = firstApp;
      if (teamState) {
        const match = team.apps.find(
          (app) => app.uuid === teamState.lastAppUuid
        );
        app = match ?? firstApp;
      }

      if (app) {
        return getMatchingTeamAppPath(team.uuid, app.uuid, location);
      } else {
        return `/${team.uuid}`;
      }
    },
    [location]
  );

  return { getAppPath, getTeamPath };
}
